import { useState, useCallback } from "react";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { TabProps } from "../types";
import { X } from "lucide-react";

const PartiesTab = ({
  formData,
  handleInputChange,
  validationErrors = {},
  readOnly = false,
}: TabProps) => {
  const readOnlyClass = readOnly ? "bg-slate-50 cursor-not-allowed" : "";

  // Parse lead_managers string into array (comma-separated, from API "managers" array)
  const managersList = (formData.lead_managers ?? "")
    .split(",")
    .map(s => s.trim())
    .filter(Boolean);

  const updateLeadManagers = useCallback(
    (newList: string[]) => {
      handleInputChange({
        target: {
          name: "lead_managers",
          value: newList.join(", "),
        },
      } as React.ChangeEvent<HTMLInputElement>);
    },
    [handleInputChange]
  );

  const removeManager = (index: number) => {
    const next = managersList.filter((_, i) => i !== index);
    updateLeadManagers(next);
  };

  const [newManagerInput, setNewManagerInput] = useState("");
  const addManager = () => {
    const name = newManagerInput.trim();
    if (!name) {
      return;
    }
    updateLeadManagers([...managersList, name]);
    setNewManagerInput("");
  };
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
          <CardTitle className="text-sm font-bold text-slate-800">
            Registrar Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 p-6">
          <div className="space-y-1">
            <Label className="text-[10px] font-bold uppercase text-slate-400">
              Registrar Name
            </Label>
            <Input
              name="registrar_name"
              value={formData.registrar_name}
              onChange={handleInputChange}
              disabled={readOnly}
              className={`h-9 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass}`}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase text-slate-400">
                Phone
              </Label>
              <Input
                name="registrar_phone"
                value={formData.registrar_phone}
                onChange={handleInputChange}
                disabled={readOnly}
                placeholder={formData.registrar_phone || "e.g. +91-1234567890"}
                className={`h-9 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                  validationErrors.registrar_phone
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
              />
              {validationErrors.registrar_phone && (
                <p className="text-xs text-red-600">
                  {validationErrors.registrar_phone}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase text-slate-400">
                Email
              </Label>
              <Input
                name="registrar_email"
                type="email"
                value={formData.registrar_email}
                onChange={handleInputChange}
                disabled={readOnly}
                placeholder={
                  formData.registrar_email || "e.g. contact@registrar.com"
                }
                className={`h-9 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${
                  validationErrors.registrar_email
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
              />
              {validationErrors.registrar_email && (
                <p className="text-xs text-red-600">
                  {validationErrors.registrar_email}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
          <CardTitle className="text-sm font-bold text-slate-800">
            Lead Managers
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 p-6">
          <div className="space-y-2">
            <Label className="text-[10px] font-bold uppercase text-slate-400">
              Lead Manager(s)
            </Label>
            <div className="flex min-h-[4.5rem] flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white p-2.5 focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/10">
              {managersList.length > 0 &&
                managersList.map((name, index) => (
                  <span
                    key={name}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm"
                  >
                    {name}
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => removeManager(index)}
                        className="rounded-full p-0.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/30"
                        aria-label={`Remove ${name}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </span>
                ))}
              {!readOnly && (
                <div className="flex min-w-[140px] flex-1 items-center gap-1">
                  <Input
                    value={newManagerInput}
                    onChange={e => setNewManagerInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addManager();
                      }
                    }}
                    onBlur={addManager}
                    placeholder="Add manager..."
                    className="h-8 min-w-0 flex-1 border-0 bg-transparent text-xs shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </div>
              )}
            </div>
            {managersList.length === 0 && readOnly && (
              <p className="text-xs text-slate-400">No lead managers added.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PartiesTab;
